import { memo, useEffect, useRef, useState } from "react";
import { PlainTooltip } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import { getCrmName } from "app/pages/sales-intelligence/utils/crm";
import type { Integration } from "app/pages/sales-intelligence/sub-modules/management/types";

import { getIntegrationNameFromUrl } from "../getIntegrationNameFromUrl";
import { usePreDefinedValidation } from "../hooks/usePreDefinedValidation";
import {
    PredefinedWrapper,
    StyledExampleInput,
    StyledErrorInfoIcon,
    StyledTooltipContentWrapper,
} from "../styles";

import { hintByDataType, validErrorTooltipsByDataType } from "./constants";
import type { ExamplePredefinedCellType } from "./types";

const PREDEFINED_NO_CRM_SELECTED = "si.management.integration.export.cell.example.predefined.noCrm.text";
const VALUE_NOT_DEFINED_ERROR = "si.management.integration.export.cell.example.crmNotDefined.error";

const ExamplePredefinedCell = ({
    value,
    dataType,
    externalField,
    isDisabled,
    index,
    focusIdx,
    onClick,
    onBlur,
    onChange,
    setting,
}: ExamplePredefinedCellType) => {
    const input = useRef<HTMLInputElement>(null);

    const [isValid, setIsValid] = useState(true);

    const { validate } = usePreDefinedValidation(setting);

    useEffect(() => {
        const isFocused = externalField && focusIdx === index;

        if (isFocused && input.current) {
            input.current.focus();
        }
    }, [externalField, focusIdx, index, input]);

    const handleChange = ({ currentTarget: { value: newValue } }: React.SyntheticEvent<HTMLInputElement>) => {
        setIsValid(validate(dataType, newValue));
        onChange(newValue);
    };

    const { integrationName } = getIntegrationNameFromUrl();

    const placeholder = !externalField
        ? translate(PREDEFINED_NO_CRM_SELECTED, {
              integrationName: getCrmName(integrationName as Integration),
          })
        : translate(hintByDataType[dataType]);

    const isNotValid = !isDisabled && !isValid;

    return (
        <PlainTooltip
            key={index}
            enabled={isDisabled}
            tooltipContent={translate(VALUE_NOT_DEFINED_ERROR)}
        >
            <PredefinedWrapper>
                <StyledExampleInput
                    ref={input}
                    onClick={onClick}
                    onBlur={onBlur}
                    placeholder={placeholder}
                    isNotValid={isNotValid}
                    disabled={isDisabled}
                    type="text"
                    alt="value"
                    onChange={handleChange}
                    value={value}
                />
                {isNotValid ? (
                    <PlainTooltip
                        enabled
                        tooltipContent={translate(validErrorTooltipsByDataType[dataType], {
                            integrationName,
                        })}
                    >
                        <StyledTooltipContentWrapper>
                            <StyledErrorInfoIcon
                                iconName="info-full"
                                size="xs"
                            />
                        </StyledTooltipContentWrapper>
                    </PlainTooltip>
                ) : null}
            </PredefinedWrapper>
        </PlainTooltip>
    );
};

export default memo(ExamplePredefinedCell);
